import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ChatHeader from "@/components/chat/ChatHeader";
import ChatMessages from "@/components/chat/ChatMessages";
import ChatInput from "@/components/chat/ChatInput";
import useChatWebSocket from "@/components/chat/useChatWebSocket";

/** In-app chat between agent and buyer for a bilateral intermediation contract.
 *  History via REST, live push via WebSocket (see useChatWebSocket). */
export default function ChatModal({ contract, open, onOpenChange }) {
  const { user } = useApp();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  const isAgent = user && contract && user.user_id === contract.agent_id;
  const counterparty = isAgent ? contract?.buyer_name : contract?.agent_name;

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  const appendUnique = useCallback((msg) => {
    setMessages((m) => (m.some((x) => x.id === msg.id) ? m : [...m, msg]));
    scrollBottom();
  }, []);

  useEffect(() => {
    if (!open || !contract) return;
    api
      .get(`/matches/intermediation/${contract.id}/messages`)
      .then(({ data }) => {
        setMessages(data);
        scrollBottom();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Chat non disponibile"));
  }, [open, contract]);

  const { status } = useChatWebSocket(contract?.id, {
    enabled: open,
    onMessage: appendUnique,
  });

  const send = async () => {
    const t = text.trim();
    if (!t || !contract) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/intermediation/${contract.id}/messages`, { text: t });
      appendUnique(data);
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-lg p-0 overflow-hidden"
        data-testid="chat-modal"
      >
        <ChatHeader counterparty={counterparty} propertyTitle={contract?.property_title} wsStatus={status} />
        <ChatMessages ref={listRef} messages={messages} myUserId={user?.user_id} />
        <ChatInput value={text} onChange={setText} onSend={send} busy={busy} />
      </DialogContent>
    </Dialog>
  );
}
